import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLimitOrderPrices } from '../../hooks/useLimitOrderPrices'
import { useAppDispatch } from '~/state/hooks'
import {
  useLimitSwapPair,
  useLimitSwapAmount,
  useLimitOrderUserConfig,
  useInputAmountVerify,
} from '~/state/dex/hooks'
import {
  updateLimitOrderUserConfig,
  updateLimitSwapAmount,
  updateLimitSwapLimitPrice,
} from '~/state/dex/actions'
import { initialState } from '~/state/dex/reducer'
import { useBreakpoint, useGA } from '~/hooks'
import trace from '~/ga'
import { toBigNumber } from '~/utils/numbers'
import { sanitizeInputValue, isValidDecimal } from '../../utils'
import { TradeDirection } from '~/components/Exchange/types'
import {
  Input,
  InputGroup,
  InputHelpText,
  InputWrapper,
  StyledLabelTokenInfo,
  ConvertIcon,
  LabelWrapper,
  OutputRateWrapper,
  RefreshIcon,
  StyledRateWithUSDWrapper,
  StyledRateWrapper,
  StyledRateUSD,
} from './styled'
import IconSwitch from '~/assets/images/switch.svg'
import IconRefresh from '~/assets/images/refresh.svg'

const RateInput: React.FC = () => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const { trackElClickCurring } = useGA()
  const dispatch = useAppDispatch()

  const [inputToken, outputToken] = useLimitSwapPair()
  const inputTokenRef = useRef(inputToken?.address)
  const outputTokenRef = useRef(outputToken?.address)
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const { isInputRateDirection } = useLimitOrderUserConfig()
  const leftToken = isInputRateDirection ? inputToken : outputToken
  const rightToken = isInputRateDirection ? outputToken : inputToken

  const verifyInputAmount = useInputAmountVerify()

  const isFirstRenderFlag = useRef(true)
  const [refreshAnimation, setRefreshAnimation] = useState(false)

  const {
    marketRate,
    displayMarketRate,
    displayLimitPrice,
    displayLimitPriceValue,
  } = useLimitOrderPrices({
    inputToken,
    outputToken,
    isInputRateDirection,
  })

  const handleLimitPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInputValue(e.target.value)

    if (!isValidDecimal(value, 12)) return
    if (!verifyInputAmount(value) && value !== '') return

    if (value === '') {
      dispatch(updateLimitSwapLimitPrice(''))
      dispatch(
        updateLimitSwapAmount({
          direction: TradeDirection.Output,
          amount: '',
        }),
      )
      return
    }

    if (inputAmount) {
      const newLimitPrice = isInputRateDirection
        ? value
        : toBigNumber(1).div(value).toString()
      const outputAmount = toBigNumber(inputAmount)
        .times(newLimitPrice)
        .decimalPlaces(outputToken?.decimals)
        .toString()
      dispatch(updateLimitSwapLimitPrice(newLimitPrice))
      dispatch(
        updateLimitSwapAmount({
          direction: TradeDirection.Output,
          amount: outputAmount,
        }),
      )
      return
    }

    const newLimitPrice = isInputRateDirection
      ? value
      : toBigNumber(1).div(value).toString()
    dispatch(updateLimitSwapLimitPrice(newLimitPrice))
  }

  const handleSwitch = () => {
    dispatch(
      updateLimitOrderUserConfig({
        isInputRateDirection: !isInputRateDirection,
      }),
    )
  }

  const handleRefresh = () => {
    setRefreshAnimation(!refreshAnimation)
    trace.click('click_limit_order_set_market_rate')
    dispatch(updateLimitSwapLimitPrice(marketRate))
    if (inputAmount && marketRate) {
      const outputAmount = toBigNumber(inputAmount)
        .times(marketRate)
        .decimalPlaces(outputToken?.decimals)
        .toString()
      dispatch(
        updateLimitSwapAmount({
          direction: TradeDirection.Output,
          amount: outputAmount,
        }),
      )
    }
  }

  // 初始化挂单价为市场价
  useEffect(() => {
    const isInitial =
      isFirstRenderFlag.current &&
      inputAmount === initialState.limitSwapInputAmount &&
      outputAmount === initialState.limitSwapOutputAmount &&
      Number(marketRate) > 0

    if (isInitial) {
      isFirstRenderFlag.current = false
      dispatch(updateLimitSwapLimitPrice(marketRate))
    }
  }, [marketRate])

  useEffect(() => {
    const isTokenChanged =
      (inputToken?.address !== inputTokenRef.current ||
        outputToken?.address !== outputTokenRef.current) &&
      inputAmount === initialState.limitSwapInputAmount &&
      outputAmount === initialState.limitSwapOutputAmount

    if (isTokenChanged && Number(marketRate) > 0) {
      dispatch(updateLimitSwapLimitPrice(marketRate))
    }

    const isDirectionChanged =
      (inputToken?.address === outputTokenRef.current ||
        outputToken?.address === inputTokenRef.current) &&
      inputAmount &&
      outputAmount

    if (isDirectionChanged && Number(marketRate) > 0) {
      const calcLimitPrice = toBigNumber(outputAmount).div(inputAmount)
      const calcScale = +calcLimitPrice < 1e-6 ? 12 : 6
      const newLimitPrice = calcLimitPrice.decimalPlaces(calcScale).toFixed()
      dispatch(updateLimitSwapLimitPrice(newLimitPrice))
    }

    inputTokenRef.current = inputToken?.address
    outputTokenRef.current = outputToken?.address
  }, [
    inputToken?.address,
    outputToken?.address,
    inputAmount,
    outputAmount,
    marketRate,
  ])

  return (
    <InputGroup>
      <LabelWrapper>
        <label>{t('limit_price')}</label>
        {isMobile && (
          <StyledLabelTokenInfo>{`(1 ${leftToken?.symbol})`}</StyledLabelTokenInfo>
        )}
      </LabelWrapper>
      <InputWrapper>
        {!isMobile && <InputHelpText>{`1 ${leftToken?.symbol}`}</InputHelpText>}
        <ConvertIcon
          src={IconSwitch}
          data-trackid="webtl_lo_rateswitch"
          onClick={(e) => trackElClickCurring(e)(handleSwitch)}
        />
        <OutputRateWrapper>
          <StyledRateWithUSDWrapper>
            <StyledRateWrapper>
              <Input
                inputMode="decimal"
                data-empty={!inputAmount && !outputAmount}
                placeholder={displayMarketRate}
                onChange={handleLimitPriceChange}
                value={displayLimitPrice}
              />
              <InputHelpText>{rightToken?.symbol}</InputHelpText>
            </StyledRateWrapper>
            <StyledRateUSD>{displayLimitPriceValue}</StyledRateUSD>
          </StyledRateWithUSDWrapper>

          <RefreshIcon
            style={{ rotate: 0 }}
            animate={{ rotate: refreshAnimation ? [0, 360] : [-1, 359] }}
            transition={{ duration: 0.4 }}
            data-trackid="webtl_lo_ratesrefresh"
            onClick={(e) => trackElClickCurring(e)(handleRefresh)}
            src={IconRefresh}
          />
        </OutputRateWrapper>
      </InputWrapper>
    </InputGroup>
  )
}

export default RateInput
